import useBranchId from "@/shared/hooks/useBranchId";
import { errorToast, formatDate } from "@/shared/utils/helpers";
import { TimeSlot } from "@/shared/utils/interfaces";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DeliveryMethods } from "../utils/enum";
import { getTimeSlotsQueryOptions } from "../utils/queryOptions";

const useTimeSlotsHook = (
  deliveryMethodId: number,
  day?: string,
  acceptAllTimesForPos?: boolean
) => {
  const branchId = useBranchId();
  const { t } = useTranslation();
  const timeSlotQueryOption = getTimeSlotsQueryOptions(
    branchId,
    deliveryMethodId
  );

  const query = useQuery<TimeSlot[]>({
    queryKey: timeSlotQueryOption.queryKey,
    queryFn: timeSlotQueryOption.queryFn,
    enabled:
      deliveryMethodId !== DeliveryMethods.POS_ORDER &&
      deliveryMethodId > 0 &&
      !acceptAllTimesForPos,
    select: (data) =>
      data === undefined ? [] : data.filter((day) => day.is_active),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (query.isError) {
      errorToast();
    }
  }, [query.isError]);

  const dateSlotsOptions = useMemo(() => {
    if (deliveryMethodId === 4) {
      const day = formatDate(new Date(), "inputDate");
      return [
        {
          label: t("today"),
          value: day,
        },
      ];
    }

    return query?.data?.map((timeSlot) => {
      const slotDate = dayjs(timeSlot.date);
      const label = slotDate.isToday()
        ? t("today")
        : slotDate.isSame(dayjs().add(1, "day"), "day")
          ? t("tomorrow")
          : formatDate(slotDate);

      return {
        label,
        value: timeSlot.date,
      };
    });
  }, [deliveryMethodId, query?.data, t]);

  const timeSlotsOptions: any = useMemo(() => {
    const retVal = [];

    query?.data?.find((timeSlot) => timeSlot.date === day)?.is_asap_active &&
      dayjs(day).isToday() &&
      retVal.push({ label: t("posOrders.asap"), value: "-1" });

    query?.data
      ?.find((timeSlot) => timeSlot.date === day)
      ?.time_slots?.filter((unit) => unit.quota_left >= 1)
      ?.forEach((unit) => {
        const isToday = dayjs(unit.start).isToday();
        const label = isToday
          ? `${unit.start_time}-${unit.end_time}`
          : `${unit.start_time}-${unit.end_time} (${formatDate(unit.start_date)})`;

        retVal.push({ label: label, value: unit.start });
      });
    return retVal;
  }, [day, query, t]);

  return {
    query,
    dateSlotsOptions,
    timeSlotsOptions,
  };
};

export default useTimeSlotsHook;
